import {
  Badge,
  Button,
  EmptyState,
  IconButton,
  Modal,
  SearchInput,
  Select,
  Surface,
  Table,
  Td,
  Th,
  Tr,
} from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RestaurantUser, RestaurantUserCandidate } from '@warehouse/shared';
import { Plus, Trash2, UserSquare } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useConfirm } from '../../../components/ConfirmDialog';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  linkRestaurantUser,
  listRestaurantUsers,
  listUserCandidates,
  unlinkRestaurantUser,
} from '../api';
import { useRestaurantOptions } from '../restaurant-options';

/**
 * `restaurant_users` administration.
 *
 * This mapping is the whole of restaurant-level authorization: it is what makes
 * a `bestallare` see one restaurant's orders and claims and not fifty
 * (DATA-MODEL `restaurant_users`, enforced in every scoped query server-side).
 * Users themselves come from EMS and are never created here — the picker only
 * links what already exists.
 */
export function RestaurantUsersPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['master-data', 'restaurant-users', q],
    queryFn: () => listRestaurantUsers(q || undefined),
  });
  useLiveQuery(['restaurants'], ['master-data', 'restaurant-users']);

  const unlink = useMutation({
    mutationFn: (row: RestaurantUser) => unlinkRestaurantUser(row.restaurantId, row.userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:restaurantUsers.removed'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const rows = data ?? [];

  return (
    <>
      {dialog}
      <PageHeader
        title={t('masterData:restaurantUsers.title')}
        subtitle={t('masterData:restaurantUsers.subtitle')}
        actions={
          <Button
            icon={<Plus />}
            onClick={() => {
              setAdding(true);
            }}
          >
            {t('masterData:restaurantUsers.add')}
          </Button>
        }
      />

      <div className="mb-4">
        <SearchInput
          variant="outline"
          className="w-full sm:w-80"
          value={q}
          onChange={setQ}
          onClear={() => {
            setQ('');
          }}
          placeholder={t('masterData:restaurantUsers.searchPlaceholder')}
          aria-label={t('masterData:restaurantUsers.searchPlaceholder')}
        />
      </div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          icon={<UserSquare />}
          title={t('masterData:restaurantUsers.empty')}
          description={t('masterData:restaurantUsers.emptyDescription')}
        />
      ) : (
        <Surface className="overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <thead>
                <tr className="border-b border-surface-border">
                  <Th>{t('masterData:restaurantUsers.columns.user')}</Th>
                  <Th>{t('masterData:restaurantUsers.columns.email')}</Th>
                  <Th>{t('masterData:restaurantUsers.columns.roles')}</Th>
                  <Th>{t('masterData:restaurantUsers.columns.restaurant')}</Th>
                  <Th>{t('masterData:restaurantUsers.columns.createdAt')}</Th>
                  <Th className="w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {rows.map((row) => (
                  <Tr key={`${row.restaurantId}:${row.userId}`}>
                    <Td>{`${row.firstName} ${row.lastName}`.trim() || '—'}</Td>
                    <Td className="text-sm">{row.email}</Td>
                    <Td>
                      <span className="flex flex-wrap gap-1">
                        {row.roleKeys.map((role) => (
                          <Badge key={role} tone="neutral" size="sm">
                            {role}
                          </Badge>
                        ))}
                      </span>
                    </Td>
                    <Td>
                      <Link className="text-brand-600 hover:underline" to={`/registers/restaurants/${row.restaurantId}`}>
                        {row.restaurantName}
                      </Link>{' '}
                      <span className="font-mono text-xs text-ink-muted">{row.restaurantCode}</span>
                    </Td>
                    <Td className="text-xs text-ink-muted">{formatDateTime(row.createdAt)}</Td>
                    <Td>
                      <IconButton
                        variant="danger"
                        aria-label={t('common:actions.remove')}
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: t('masterData:restaurantUsers.confirmRemove.title'),
                              message: t('masterData:restaurantUsers.confirmRemove.message', {
                                email: row.email,
                                restaurant: row.restaurantName,
                              }),
                            });
                            if (ok) unlink.mutate(row);
                          })();
                        }}
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Surface>
      )}

      {adding ? (
        <LinkUserModal
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </>
  );
}

function LinkUserModal({ onClose }: { onClose: () => void }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const restaurantOptions = useRestaurantOptions();

  const { data } = useQuery({
    queryKey: ['master-data', 'user-candidates', q],
    queryFn: () => listUserCandidates(q || undefined),
  });

  const link = useMutation({
    mutationFn: (userId: string) => linkRestaurantUser({ userId, restaurantId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:restaurantUsers.added'));
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const candidates: RestaurantUserCandidate[] = data ?? [];

  return (
    <Modal open onClose={onClose} title={t('masterData:restaurantUsers.add')} width={620}>
      <div className="flex flex-col gap-4">
        <Select
          label={t('masterData:restaurantUsers.fields.restaurant')}
          options={[{ value: '', label: '—' }, ...restaurantOptions]}
          value={restaurantId}
          onChange={(event) => {
            setRestaurantId(event.target.value);
          }}
        />

        <div>
          <p className="mb-1.5 text-sm text-ink">{t('masterData:restaurantUsers.fields.user')}</p>
          <p className="mb-2 text-xs text-ink-muted">{t('masterData:restaurantUsers.hints.user')}</p>
          <SearchInput
            variant="outline"
            value={q}
            onChange={setQ}
            onClear={() => {
              setQ('');
            }}
            placeholder={t('masterData:restaurantUsers.searchPlaceholder')}
            aria-label={t('masterData:restaurantUsers.searchPlaceholder')}
          />
        </div>

        <Surface variant="subtle" className="max-h-72 overflow-y-auto scrollbar-thin">
          {candidates.length === 0 ? (
            <p className="p-3 text-sm text-ink-muted">{t('masterData:restaurantUsers.noCandidates')}</p>
          ) : (
            <ul className="divide-y divide-surface-border">
              {candidates.map((candidate) => {
                const already = restaurantId !== '' && candidate.restaurantIds.includes(restaurantId);
                return (
                  <li key={candidate.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink">
                        {`${candidate.firstName} ${candidate.lastName}`.trim() || candidate.email}
                      </p>
                      <p className="truncate text-xs text-ink-muted">{candidate.email}</p>
                    </div>
                    {already ? (
                      <Badge tone="neutral" size="sm">
                        {t('masterData:restaurantUsers.alreadyLinked')}
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={restaurantId === ''}
                        loading={link.isPending}
                        onClick={() => {
                          link.mutate(candidate.id);
                        }}
                      >
                        {t('common:actions.add')}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Surface>
      </div>
    </Modal>
  );
}
